import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Section, SectionItem, SEO } from '@/components';
import {
  blogPosts,
  blogCategories,
  getFeaturedPost,
  getPopularPosts,
  formatDate,
  type BlogCategory,
  type BlogPost,
} from '@/data/blog';

export function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState<BlogCategory | 'all'>('all');
  const featuredPost = getFeaturedPost();
  const popularPosts = getPopularPosts(4);

  const filteredPosts = selectedCategory === 'all'
    ? blogPosts.filter((p) => !p.featured)
    : blogPosts.filter((p) => p.category === selectedCategory && !p.featured);

  return (
    <>
      <SEO
        title="Blog - Tips for Growing Your Service Business"
        description="Expert insights on growing and running a successful service business. Tips on operations, technology, growth strategies, and industry trends."
        keywords={[
          'service business blog',
          'HVAC business tips',
          'plumbing business advice',
          'field service management tips',
          'service company growth',
          'business operations',
        ]}
        ogImage="/og/blog.png"
        type="website"
      />

      {/* Featured Post Hero */}
      {featuredPost && (
        <section className="bg-ink pt-32 pb-16">
          <div className="container-marketing">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="text-center mb-8">
                <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium mb-4">
                  Featured Article
                </span>
                <h1 className="font-display text-h1 text-paper mb-4">Omniforge Blog</h1>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                  Insights, strategies, and best practices for service companies
                </p>
              </div>

              <FeaturedPostCard post={featuredPost} />
            </motion.div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <Section background="paper">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Column */}
          <div className="lg:col-span-2">
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 mb-8">
              <CategoryButton
                label="All"
                isActive={selectedCategory === 'all'}
                onClick={() => setSelectedCategory('all')}
              />
              {blogCategories.map((category) => (
                <CategoryButton
                  key={category.id}
                  label={category.name}
                  isActive={selectedCategory === category.id}
                  onClick={() => setSelectedCategory(category.id)}
                />
              ))}
            </div>

            {/* Blog Post Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {filteredPosts.map((post) => (
                <SectionItem key={post.id}>
                  <BlogPostCard post={post} />
                </SectionItem>
              ))}
            </div>

            {/* Pagination Placeholder */}
            <div className="mt-12 flex justify-center">
              <Pagination currentPage={1} totalPages={3} />
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-8">
              {/* Popular Posts */}
              <div className="bg-cream rounded-2xl p-6">
                <h3 className="font-heading font-semibold text-lg mb-4">Popular Articles</h3>
                <div className="space-y-4">
                  {popularPosts.map((post, index) => (
                    <PopularPostItem key={post.id} post={post} index={index + 1} />
                  ))}
                </div>
              </div>

              {/* Newsletter Signup */}
              <div className="bg-ink rounded-2xl p-6">
                <h3 className="font-heading font-semibold text-lg text-paper mb-2">
                  Subscribe to our newsletter
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Get the latest articles and industry insights delivered to your inbox.
                </p>
                <SidebarNewsletterForm />
              </div>

              {/* Categories */}
              <div className="bg-cream rounded-2xl p-6">
                <h3 className="font-heading font-semibold text-lg mb-4">Categories</h3>
                <ul className="space-y-2">
                  {blogCategories.map((category) => {
                    const count = blogPosts.filter((p) => p.category === category.id).length;
                    return (
                      <li key={category.id}>
                        <button
                          onClick={() => setSelectedCategory(category.id)}
                          className="flex items-center justify-between w-full py-2 px-3 rounded-lg hover:bg-white transition-colors text-left"
                        >
                          <span>{category.name}</span>
                          <span className="text-sm text-muted bg-white px-2 py-0.5 rounded">
                            {count}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </Section>
    </>
  );
}

// ============================================
// FEATURED POST CARD
// ============================================

interface FeaturedPostCardProps {
  post: BlogPost;
}

function FeaturedPostCard({ post }: FeaturedPostCardProps) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block bg-charcoal rounded-2xl overflow-hidden hover:bg-slate transition-colors"
    >
      <div className="grid md:grid-cols-2 gap-0">
        <div className="relative aspect-video md:aspect-auto">
          <img
            src={post.imageUrl}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-8 flex flex-col justify-center">
          <div className="flex items-center gap-3 text-sm text-gray-400 mb-4">
            <CategoryBadge category={post.category} />
            <span>{formatDate(post.publishedAt)}</span>
            <span>|</span>
            <span>{post.readTime} min read</span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-paper mb-4 group-hover:text-accent transition-colors">
            {post.title}
          </h2>
          <p className="text-gray-300 mb-6 line-clamp-3">{post.excerpt}</p>
          <div className="flex items-center gap-3">
            <img
              src={post.author.imageUrl}
              alt={post.author.name}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <div className="font-medium text-paper">{post.author.name}</div>
              <div className="text-sm text-gray-400">{post.author.role}</div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ============================================
// BLOG POST CARD
// ============================================

interface BlogPostCardProps {
  post: BlogPost;
}

function BlogPostCard({ post }: BlogPostCardProps) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={post.imageUrl}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4">
          <CategoryBadge category={post.category} />
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-xs text-muted mb-2">
          <span>{formatDate(post.publishedAt)}</span>
          <span>|</span>
          <span>{post.readTime} min read</span>
        </div>
        <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-accent transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="text-muted text-sm line-clamp-2">{post.excerpt}</p>
      </div>
    </Link>
  );
}

// ============================================
// POPULAR POST ITEM
// ============================================

interface PopularPostItemProps {
  post: BlogPost;
  index: number;
}

function PopularPostItem({ post, index }: PopularPostItemProps) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex gap-3 items-start"
    >
      <span className="text-2xl font-bold text-gray-200 group-hover:text-accent transition-colors">
        {String(index).padStart(2, '0')}
      </span>
      <div className="flex-1">
        <h4 className="font-medium text-sm line-clamp-2 group-hover:text-accent transition-colors">
          {post.title}
        </h4>
        <span className="text-xs text-muted">{post.readTime} min read</span>
      </div>
    </Link>
  );
}

// ============================================
// CATEGORY COMPONENTS
// ============================================

interface CategoryButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function CategoryButton({ label, isActive, onClick }: CategoryButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
        isActive
          ? 'bg-accent text-white'
          : 'bg-cream hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}

interface CategoryBadgeProps {
  category: BlogCategory;
}

function CategoryBadge({ category }: CategoryBadgeProps) {
  const colors: Record<BlogCategory, string> = {
    growth: 'bg-green-100 text-green-800',
    operations: 'bg-blue-100 text-blue-800',
    technology: 'bg-purple-100 text-purple-800',
    'industry-news': 'bg-orange-100 text-orange-800',
  };

  const labels: Record<BlogCategory, string> = {
    growth: 'Growth',
    operations: 'Operations',
    technology: 'Technology',
    'industry-news': 'Industry News',
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[category]}`}>
      {labels[category]}
    </span>
  );
}

// ============================================
// PAGINATION
// ============================================

interface PaginationProps {
  currentPage: number;
  totalPages: number;
}

function Pagination({ currentPage, totalPages }: PaginationProps) {
  return (
    <div className="flex items-center gap-2">
      <button
        disabled={currentPage === 1}
        className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeftIcon className="w-5 h-5" />
      </button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <button
          key={page}
          className={`w-10 h-10 rounded-lg font-medium transition-colors ${
            page === currentPage
              ? 'bg-accent text-white'
              : 'hover:bg-gray-50'
          }`}
        >
          {page}
        </button>
      ))}
      <button
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronRightIcon className="w-5 h-5" />
      </button>
    </div>
  );
}

// ============================================
// SIDEBAR NEWSLETTER FORM
// ============================================

function SidebarNewsletterForm() {
  return (
    <form className="space-y-3">
      <input
        type="email"
        placeholder="Enter your email"
        className="w-full px-4 py-3 rounded-lg bg-charcoal border border-gray-700 text-paper placeholder-gray-400 focus:outline-none focus:border-accent transition-colors"
      />
      <button type="submit" className="w-full btn-primary py-3">
        Subscribe
      </button>
    </form>
  );
}

// ============================================
// ICONS
// ============================================

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}
